/**
 * Month / week / day scheduling for journey steps. The backend stores a single
 * `available_offset_days` (days after the employee's start date); the UI lets you
 * pick month/week/day and derives the offset from it (4 weeks/month, 7 days/week).
 */
const DAYS_PER_WEEK = 7;
const WEEKS_PER_MONTH = 4;
const DAYS_PER_MONTH = DAYS_PER_WEEK * WEEKS_PER_MONTH;

export interface MWD {
  month: number;
  week: number;
  day: number;
}

export function offsetToMWD(offset: number): MWD {
  const o = Math.max(0, Math.floor(offset || 0));
  const rem = o % DAYS_PER_MONTH;
  return {
    month: Math.floor(o / DAYS_PER_MONTH) + 1,
    week: Math.floor(rem / DAYS_PER_WEEK) + 1,
    day: (rem % DAYS_PER_WEEK) + 1,
  };
}

export function mwdToOffset(month: number, week: number, day: number): number {
  const m = Math.max(1, Math.floor(month) || 1);
  const w = Math.min(WEEKS_PER_MONTH, Math.max(1, Math.floor(week) || 1));
  const d = Math.min(DAYS_PER_WEEK, Math.max(1, Math.floor(day) || 1));
  return ((m - 1) * WEEKS_PER_MONTH + (w - 1)) * DAYS_PER_WEEK + (d - 1);
}

/** "Maand 1 · Week 2 · Dag 3" — full schedule label for a step. */
export function scheduleLabel(offset: number, t: (nl: string, en: string) => string): string {
  const { month, week, day } = offsetToMWD(offset);
  return `${t("Maand", "Month")} ${month} · ${t("Week", "Week")} ${week} · ${t("Dag", "Day")} ${day}`;
}

/** "Maand 1 · Week 2" — grouping label (month + week) for the program timeline. */
export function groupLabel(offset: number, t: (nl: string, en: string) => string): string {
  const { month, week } = offsetToMWD(offset);
  return `${t("Maand", "Month")} ${month} · ${t("Week", "Week")} ${week}`;
}
